import { FormEvent, useEffect, useMemo, useState } from 'react';
import { LayoutGrid, X } from 'lucide-react';
import CompletionModal from './CompletionModal';
import ReadLabelCombobox from './ReadLabelCombobox';
import type { ResolvedBox } from '../../services/sensorBoxBoxes';
import {
  ArrayAssemblyUnavailableError,
  assembleSensorArray,
  createSensorSpecification,
  inferArraySpecType,
  loadArrayWorkspace,
  specsMatchingLabel,
  type ApiReadLabel,
  type ArrayAssemblyResult,
  type ArraySpecCard,
  type ArraySpecType,
  type ChosenSensorSpec,
  type EnrichedSensorSpec,
  type NewArrayAssembly,
  type SensorValueType,
} from '../../services/sensorBoxArrays';

type Mode = 'existing' | 'new';

type ArrayStepProps = {
  box: ResolvedBox;
  onCreateAnother?: () => void;
};

const inputClass =
  'w-full h-11 rounded-lg border border-gray-200 bg-white px-3 text-sm text-gray-900 placeholder:text-gray-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 focus-visible:ring-offset-1';
const inputErrorClass = 'border-red-300 focus-visible:ring-red-400';
const SENSOR_TYPES: SensorValueType[] = ['float', 'int', 'char', 'media'];
const ARRAY_TYPES: ArraySpecType[] = ['data', 'media', 'multi'];

function nextKey(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function sensorSummary(spec: EnrichedSensorSpec): string {
  const unit = spec.unitAbbreviation || spec.unit;
  return unit ? `${spec.readValLabel} (${spec.type}, ${unit})` : `${spec.readValLabel} (${spec.type})`;
}

function formatApiError(error: unknown, fallback: string): string {
  const message = error instanceof Error ? error.message : fallback;
  if (/401|403|unauthorized|forbidden/i.test(message)) {
    return 'Your session expired or you do not have permission. Sign in again and retry.';
  }
  if (/failed to fetch|network/i.test(message)) {
    return 'Could not reach the Seawall API. Check your connection and try again.';
  }
  return message;
}

export default function ArrayStep({ box, onCreateAnother }: ArrayStepProps) {
  const [mode, setMode] = useState<Mode>('existing');
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [labels, setLabels] = useState<ApiReadLabel[]>([]);
  const [sensorSpecs, setSensorSpecs] = useState<EnrichedSensorSpec[]>([]);
  const [arraySpecCards, setArraySpecCards] = useState<ArraySpecCard[]>([]);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [pickedSpecId, setPickedSpecId] = useState<string | null>(null);

  const [draftSensors, setDraftSensors] = useState<ChosenSensorSpec[]>([]);
  const [arrayType, setArrayType] = useState<ArraySpecType>('data');
  const [arrayVersion, setArrayVersion] = useState('1');

  const [specType, setSpecType] = useState<SensorValueType>('float');
  const [specVersion, setSpecVersion] = useState('1');
  const [labelMode, setLabelMode] = useState<'existing' | 'new'>('existing');
  const [selectedLabelId, setSelectedLabelId] = useState('');
  const [newLabel, setNewLabel] = useState('');
  const [newUnit, setNewUnit] = useState('');
  const [newUnitAbbr, setNewUnitAbbr] = useState('');
  const [addError, setAddError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [confirmingCreate, setConfirmingCreate] = useState(false);

  const [formError, setFormError] = useState<string | null>(null);
  const [pendingAssembly, setPendingAssembly] = useState<NewArrayAssembly | null>(null);
  const [assemblyResult, setAssemblyResult] = useState<ArrayAssemblyResult | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  const reloadWorkspace = async () => {
    const data = await loadArrayWorkspace();
    setLabels(data.labels);
    setSensorSpecs(data.sensorSpecs);
    setArraySpecCards(data.arraySpecCards);
  };

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setLoadError(null);
      try {
        const data = await loadArrayWorkspace();
        if (cancelled) return;
        setLabels(data.labels);
        setSensorSpecs(data.sensorSpecs);
        setArraySpecCards(data.arraySpecCards);
      } catch {
        if (!cancelled) {
          setLoadError('Could not load sensor array data from the Seawall API.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  const selectedLabel = labels.find((label) => String(label.ID) === selectedLabelId);
  const typedLabel = labelMode === 'existing' ? selectedLabel?.readValLabel ?? '' : newLabel;
  const suggestions = useMemo(
    () => specsMatchingLabel(sensorSpecs, typedLabel),
    [sensorSpecs, typedLabel],
  );

  useEffect(() => {
    setArrayType(inferArraySpecType(draftSensors));
  }, [draftSensors]);

  const switchMode = (next: Mode) => {
    if (adding || submitting) return;
    setMode(next);
    setFormError(null);
    setPendingAssembly(null);
    setAssemblyResult(null);
    setModalOpen(false);
    setConfirmingCreate(false);
  };

  const addExistingSpec = (spec: EnrichedSensorSpec) => {
    setDraftSensors((current) => [
      ...current,
      { ...spec, key: nextKey(), createdNow: false },
    ]);
    setAddError(null);
    setConfirmingCreate(false);
  };

  const handleCreateSpec = (event: FormEvent) => {
    event.preventDefault();
    setAddError(null);

    const version = Number(specVersion);
    if (!Number.isInteger(version) || version < 1) {
      setAddError('Version must be a positive integer.');
      return;
    }

    if (labelMode === 'existing') {
      if (!selectedLabel) {
        setAddError('Choose an existing read label, or create a new one.');
        return;
      }
      const match = suggestions.find((spec) => spec.type === specType && spec.version === version);
      if (match) {
        addExistingSpec(match);
        return;
      }
    } else if (!newLabel.trim() || !newUnit.trim() || !newUnitAbbr.trim()) {
      setAddError('New labels need a name, unit, and abbreviation.');
      return;
    }

    setConfirmingCreate(true);
  };

  const performCreateSpec = async () => {
    const version = Number(specVersion);
    setAdding(true);
    setAddError(null);
    try {
      const newId = await createSensorSpecification(
        labelMode === 'existing' && selectedLabel
          ? {
              type: specType,
              version,
              readValLabelID: Number(selectedLabel.ID),
            }
          : {
              type: specType,
              version,
              readValLabelObj: {
                readValLabel: newLabel.trim(),
                unit: newUnit.trim(),
                unitAbbreviation: newUnitAbbr.trim(),
              },
            },
      );

      const created: ChosenSensorSpec = {
        key: nextKey(),
        id: newId,
        type: specType,
        version,
        labelId: labelMode === 'existing' ? selectedLabelId : null,
        readValLabel:
          labelMode === 'existing' ? selectedLabel?.readValLabel ?? newLabel.trim() : newLabel.trim(),
        unit: labelMode === 'existing' ? selectedLabel?.unit ?? '' : newUnit.trim(),
        unitAbbreviation:
          labelMode === 'existing' ? selectedLabel?.unitAbbreviation ?? '' : newUnitAbbr.trim(),
        createdNow: true,
      };
      setDraftSensors((current) => [...current, created]);
      setConfirmingCreate(false);
      await reloadWorkspace();
    } catch (error) {
      setAddError(formatApiError(error, 'Could not create that sensor specification.'));
    } finally {
      setAdding(false);
    }
  };

  const submitAssembly = async (
    spec: { id?: string; type: ArraySpecType; version: number },
    sensors: EnrichedSensorSpec[],
  ) => {
    setFormError(null);
    setPendingAssembly(null);
    setAssemblyResult(null);
    if (sensors.length === 0) {
      setFormError('Add at least one sensor specification before creating the array.');
      return;
    }

    setSubmitting(true);
    try {
      const result = await assembleSensorArray(box.id, spec, sensors);
      setAssemblyResult(result);
      setModalOpen(true);
    } catch (error) {
      if (error instanceof ArrayAssemblyUnavailableError) {
        setPendingAssembly(error.payload);
        setFormError(error.message);
        setModalOpen(true);
      } else {
        setFormError(formatApiError(error, 'Could not assemble the sensor array.'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleUseExisting = () => {
    const card = arraySpecCards.find((item) => item.id === pickedSpecId);
    if (!card) {
      setFormError('Select an existing array specification to continue.');
      return;
    }
    void submitAssembly(
      { id: card.id, type: (ARRAY_TYPES.includes(card.type as ArraySpecType) ? card.type : 'data') as ArraySpecType, version: card.version },
      card.sensors,
    );
  };

  const handleCreateNewArray = (event: FormEvent) => {
    event.preventDefault();
    const version = Number(arrayVersion);
    if (!Number.isInteger(version) || version < 1) {
      setFormError('Array specification version must be a positive integer.');
      return;
    }
    void submitAssembly(
      { type: arrayType, version },
      draftSensors,
    );
  };

  const pickedCard = arraySpecCards.find((item) => item.id === pickedSpecId);

  return (
    <>
      <div className="bg-white rounded-2xl shadow-lg">
        <div className="grid grid-cols-2 border-b border-gray-100">
          <button
            type="button"
            onClick={() => switchMode('existing')}
            disabled={adding || submitting}
            className={`py-4 text-sm font-semibold transition-colors disabled:opacity-60 ${
              mode === 'existing'
                ? 'text-teal-600 border-b-2 border-teal-500 bg-teal-50/50'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Existing specification
          </button>
          <button
            type="button"
            onClick={() => switchMode('new')}
            disabled={adding || submitting}
            className={`py-4 text-sm font-semibold transition-colors disabled:opacity-60 ${
              mode === 'new'
                ? 'text-teal-600 border-b-2 border-teal-500 bg-teal-50/50'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            New specification
          </button>
        </div>

        <div className="p-6 sm:p-8">
          {loadError && (
            <div className="mb-5 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3">
              {loadError}
            </div>
          )}

          {mode === 'existing' ? (
            <div className="space-y-4">
              <p className="text-gray-600 text-sm">
                Reuse an array specification already in the system. Sensors listed here come from
                deployments that already use that specification.
              </p>

              {loading ? (
                <p className="text-sm text-gray-500 py-8 text-center">Loading array specifications…</p>
              ) : arraySpecCards.length === 0 ? (
                <p className="text-sm text-gray-500 py-8 text-center">
                  No array specifications found. Define a new one instead.
                </p>
              ) : (
                <ul className="space-y-2 max-h-96 overflow-y-auto">
                  {arraySpecCards.map((card) => {
                    const selected = pickedSpecId === card.id;
                    const expanded = expandedId === card.id || selected;
                    return (
                      <li key={card.id}>
                        <button
                          type="button"
                          onClick={() => {
                            setPickedSpecId(card.id);
                            setExpandedId(card.id);
                            setFormError(null);
                          }}
                          className={`w-full text-left rounded-xl border px-4 py-3 transition-colors ${
                            selected
                              ? 'border-teal-500 bg-teal-50'
                              : 'border-gray-200 hover:border-teal-300 hover:bg-teal-50/40'
                          }`}
                        >
                          <div className="flex items-start gap-3">
                            <LayoutGrid className={`mt-0.5 h-4 w-4 shrink-0 ${selected ? 'text-teal-600' : 'text-gray-400'}`} />
                            <div className="min-w-0">
                              <p className="font-medium text-gray-900">
                                Specification {card.id}
                                <span className="ml-2 text-xs font-normal text-gray-500">
                                  {card.type} · v{card.version}
                                </span>
                              </p>
                              {expanded && (
                                <ul className="mt-2 space-y-1">
                                  {card.sensors.length === 0 ? (
                                    <li className="text-sm text-gray-500">
                                      No deployed sensors found for this specification yet.
                                    </li>
                                  ) : (
                                    card.sensors.map((sensor) => (
                                      <li key={sensor.id} className="text-sm text-gray-700">
                                        {sensorSummary(sensor)}
                                        <span className="text-gray-400"> · spec ID {sensor.id}</span>
                                      </li>
                                    ))
                                  )}
                                </ul>
                              )}
                              <p className="text-xs text-gray-500 mt-1">
                                {card.physicalSensorCount} physical sensor
                                {card.physicalSensorCount === 1 ? '' : 's'} deployed
                              </p>
                            </div>
                          </div>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}

              {formError && !modalOpen && (
                <div className="rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3">
                  {formError}
                </div>
              )}

              <button
                type="button"
                onClick={handleUseExisting}
                disabled={!pickedSpecId || loading || submitting || adding}
                className="w-full h-11 rounded-lg bg-[#005E6E] text-white font-semibold hover:bg-[#004957] transition-colors shadow-md disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {submitting ? 'Submitting…' : 'Use this specification'}
              </button>
            </div>
          ) : (
            <form onSubmit={handleCreateNewArray} className="space-y-6" noValidate>
              <p className="text-gray-600 text-sm">
                Build the list of sensors for this array. Existing specifications are suggested by
                read label. New specifications are created immediately. Submitting then creates the
                array specification, the array on this box, and each sensor.
              </p>

              {draftSensors.length > 0 && (
                <ul className="space-y-2">
                  {draftSensors.map((sensor) => (
                    <li
                      key={sensor.key}
                      className="flex items-start justify-between gap-3 rounded-xl border border-gray-200 px-4 py-3"
                    >
                      <div>
                        <p className="font-medium text-gray-900">{sensorSummary(sensor)}</p>
                        <p className="text-xs text-gray-500 mt-1">
                          Spec ID {sensor.id}
                          {sensor.createdNow ? ' · created just now' : ' · reused'}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() =>
                          setDraftSensors((current) => current.filter((item) => item.key !== sensor.key))
                        }
                        className="text-gray-400 hover:text-gray-700"
                        aria-label={`Remove ${sensor.readValLabel}`}
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}

              <div className="rounded-xl border border-dashed border-gray-300 p-4 space-y-4">
                <p className="text-sm font-medium text-gray-800">Add a sensor</p>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="specType" className="block text-sm font-medium text-gray-700 mb-1.5">
                      Type
                    </label>
                    <select
                      id="specType"
                      value={specType}
                      onChange={(event) => setSpecType(event.target.value as SensorValueType)}
                      className={inputClass}
                    >
                      {SENSOR_TYPES.map((type) => (
                        <option key={type} value={type}>
                          {type}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label htmlFor="specVersion" className="block text-sm font-medium text-gray-700 mb-1.5">
                      Version
                    </label>
                    <input
                      id="specVersion"
                      value={specVersion}
                      onChange={(event) => setSpecVersion(event.target.value)}
                      className={inputClass}
                    />
                  </div>
                </div>

                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setLabelMode('existing');
                      setConfirmingCreate(false);
                    }}
                    className={`flex-1 h-10 rounded-lg text-sm font-medium ${
                      labelMode === 'existing'
                        ? 'bg-teal-50 text-teal-700 border border-teal-300'
                        : 'bg-white text-gray-600 border border-gray-200'
                    }`}
                  >
                    Existing label
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setLabelMode('new');
                      setConfirmingCreate(false);
                    }}
                    className={`flex-1 h-10 rounded-lg text-sm font-medium ${
                      labelMode === 'new'
                        ? 'bg-teal-50 text-teal-700 border border-teal-300'
                        : 'bg-white text-gray-600 border border-gray-200'
                    }`}
                  >
                    New label
                  </button>
                </div>

                {labelMode === 'existing' ? (
                  <div>
                    <label htmlFor="readLabel" className="block text-sm font-medium text-gray-700 mb-1.5">
                      Read label
                    </label>
                    <ReadLabelCombobox
                      id="readLabel"
                      labels={labels}
                      value={selectedLabelId}
                      onChange={(labelId) => {
                        setSelectedLabelId(labelId);
                        setConfirmingCreate(false);
                      }}
                      disabled={adding || submitting}
                    />
                  </div>
                ) : (
                  <div className="grid sm:grid-cols-3 gap-3">
                    <input
                      value={newLabel}
                      onChange={(event) => setNewLabel(event.target.value)}
                      placeholder="turbidity"
                      className={inputClass}
                      aria-label="New read label"
                    />
                    <input
                      value={newUnit}
                      onChange={(event) => setNewUnit(event.target.value)}
                      placeholder="Nephelometric Turbidity Unit"
                      className={inputClass}
                      aria-label="Unit name"
                    />
                    <input
                      value={newUnitAbbr}
                      onChange={(event) => setNewUnitAbbr(event.target.value)}
                      placeholder="NTU"
                      className={inputClass}
                      aria-label="Unit abbreviation"
                    />
                  </div>
                )}

                {suggestions.length > 0 && (
                  <div className="rounded-lg bg-teal-50 border border-teal-200 px-4 py-3">
                    <p className="text-sm text-teal-800 mb-2">
                      A specification with this read label already exists. Reuse it instead of creating
                      another.
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {suggestions.map((spec) => (
                        <button
                          key={spec.id}
                          type="button"
                          onClick={() => addExistingSpec(spec)}
                          className="rounded-full bg-white px-3 py-1 text-xs font-medium text-teal-800 border border-teal-200 hover:bg-teal-100"
                        >
                          Reuse spec {spec.id}: {sensorSummary(spec)}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {addError && <p className="text-sm text-red-600">{addError}</p>}

                {confirmingCreate ? (
                  <div className="rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 space-y-3">
                    <p className="text-sm text-amber-900">
                      This will create a new sensor specification on the live Seawall API. You cannot
                      undo this from the website.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-2">
                      <button
                        type="button"
                        onClick={() => void performCreateSpec()}
                        disabled={adding}
                        className="flex-1 h-10 rounded-lg bg-[#005E6E] text-white font-semibold hover:bg-[#004957] disabled:opacity-60 disabled:cursor-not-allowed"
                      >
                        {adding ? 'Saving specification…' : 'Yes, create it'}
                      </button>
                      <button
                        type="button"
                        onClick={() => setConfirmingCreate(false)}
                        disabled={adding}
                        className="flex-1 h-10 rounded-lg border border-gray-300 text-gray-700 font-semibold hover:bg-gray-50 disabled:opacity-60"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={(event) => handleCreateSpec(event)}
                    disabled={adding || submitting}
                    className="w-full h-10 rounded-lg border border-[#005E6E] text-[#005E6E] font-semibold hover:bg-teal-50 disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {suggestions.length > 0 ? 'Create different specification' : 'Add sensor specification'}
                  </button>
                )}
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="arrayType" className="block text-sm font-medium text-gray-700 mb-1.5">
                    Array type
                  </label>
                  <select
                    id="arrayType"
                    value={arrayType}
                    onChange={(event) => setArrayType(event.target.value as ArraySpecType)}
                    className={inputClass}
                  >
                    {ARRAY_TYPES.map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="arrayVersion" className="block text-sm font-medium text-gray-700 mb-1.5">
                    Array version
                  </label>
                  <input
                    id="arrayVersion"
                    value={arrayVersion}
                    onChange={(event) => setArrayVersion(event.target.value)}
                    className={`${inputClass} ${formError && !Number(arrayVersion) ? inputErrorClass : ''}`}
                  />
                </div>
              </div>

              {formError && !modalOpen && (
                <div className="rounded-lg bg-amber-50 border border-amber-200 text-amber-900 text-sm px-4 py-3">
                  {formError}
                </div>
              )}

              <button
                type="submit"
                disabled={submitting || adding || draftSensors.length === 0}
                className="w-full h-11 rounded-lg bg-[#005E6E] text-white font-semibold hover:bg-[#004957] transition-colors shadow-md disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {submitting ? 'Submitting…' : 'Create sensor array'}
              </button>
            </form>
          )}
        </div>
      </div>

      {modalOpen && (assemblyResult || pendingAssembly) && (
        <CompletionModal
          boxId={box.id}
          result={assemblyResult}
          pending={pendingAssembly}
          extraSensors={mode === 'existing' ? pickedCard?.sensors : draftSensors}
          onClose={() => {
            setModalOpen(false);
            setPendingAssembly(null);
          }}
          onCreateAnother={onCreateAnother}
        />
      )}
    </>
  );
}
